import { PaginationParams } from '@core/api/api-entity.service';

export const FUND_DDQ_RUN_FILTER = {
  ALL: 'all',
  MY_ORGANIZATION: 'my_organization',
  RECENTLY_VIEWED: 'recently_viewed',
  RECENTLY_VIEWED_BY_ORGANIZATION: 'recently_viewed_by_organization',
} as const;

export type FundDDQRunFilter = (typeof FUND_DDQ_RUN_FILTER)[keyof typeof FUND_DDQ_RUN_FILTER];

export type FundDDQRunApiFilter = Exclude<FundDDQRunFilter, typeof FUND_DDQ_RUN_FILTER.ALL>;

export const DEFAULT_FUND_DDQ_RUN_RECENT_DAYS = 30;
export const MIN_FUND_DDQ_RUN_RECENT_DAYS = 1;
export const MAX_FUND_DDQ_RUN_RECENT_DAYS = 365;

export interface FundDDQRunListFilterParams {
  filter?: FundDDQRunFilter;
  recentDays?: number;
}

const ACTIVITY_FILTERS: ReadonlySet<FundDDQRunFilter> = new Set([
  FUND_DDQ_RUN_FILTER.RECENTLY_VIEWED,
  FUND_DDQ_RUN_FILTER.RECENTLY_VIEWED_BY_ORGANIZATION,
]);

const API_FILTERS: ReadonlySet<FundDDQRunApiFilter> = new Set([
  FUND_DDQ_RUN_FILTER.MY_ORGANIZATION,
  FUND_DDQ_RUN_FILTER.RECENTLY_VIEWED,
  FUND_DDQ_RUN_FILTER.RECENTLY_VIEWED_BY_ORGANIZATION,
]);

export function isFundDDQRunActivityFilter(filter?: FundDDQRunFilter | null): boolean {
  return !!filter && ACTIVITY_FILTERS.has(filter);
}

/**
 * Maps UI filter state to the backend `filter` query value.
 * `all` and unknown values are omitted so the API is never sent an unsupported filter.
 */
export function toFundDDQRunApiFilter(filter?: FundDDQRunFilter | null): FundDDQRunApiFilter | undefined {
  if (!filter || filter === FUND_DDQ_RUN_FILTER.ALL) {
    return undefined;
  }
  return API_FILTERS.has(filter) ? filter : undefined;
}

export function normalizeFundDDQRunRecentDays(recentDays?: number): number {
  if (recentDays == null || Number.isNaN(Number(recentDays))) {
    return DEFAULT_FUND_DDQ_RUN_RECENT_DAYS;
  }
  const value = Math.floor(Number(recentDays));
  if (value < MIN_FUND_DDQ_RUN_RECENT_DAYS) {
    return MIN_FUND_DDQ_RUN_RECENT_DAYS;
  }
  if (value > MAX_FUND_DDQ_RUN_RECENT_DAYS) {
    return MAX_FUND_DDQ_RUN_RECENT_DAYS;
  }
  return value;
}

export function buildFundDDQRunListCacheKey(
  paginationParams?: PaginationParams,
  search?: string,
  filterParams?: FundDDQRunListFilterParams,
): string {
  const page = paginationParams?.page ?? '';
  const pageSize = paginationParams?.pageSize ?? '';
  const query = (search ?? '').trim();
  const apiFilter = toFundDDQRunApiFilter(filterParams?.filter) ?? FUND_DDQ_RUN_FILTER.ALL;
  return `page=${page}&pageSize=${pageSize}&search=${query}&filter=${apiFilter}`;
}
